import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import { adminService } from '../../../services/adminService';

// Terminal statuses — visually dimmed
const TERMINAL_STATUSES = ['livre', 'annule', 'delivered', 'exception'];

const ShipmentTableRow = ({ shipment, onStatusUpdate, onDeleted, onArchived, userRole }) => {
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);

  // Modal states
  const [showArchiveConfirm, setShowArchiveConfirm] = useState(false);
  const [showRestoreConfirm, setShowRestoreConfirm] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteBlockedByHistory, setDeleteBlockedByHistory] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [actionError, setActionError] = useState('');

  const isAdmin = userRole === 'admin' || userRole === 'super_admin';
  const isArchived = shipment?.isArchived;
  const isTerminal = TERMINAL_STATUSES?.includes(shipment?.status);

  // Close menu on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (menuRef?.current && !menuRef?.current?.contains(e?.target)) {
        setMenuOpen(false);
      }
    };
    if (menuOpen) document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [menuOpen]);

  // ─── Row background based on status / archive ────────────────────────────
  const getRowClass = () => {
    if (isArchived) return 'bg-gray-100 opacity-70';
    if (shipment?.status === 'livre' || shipment?.status === 'delivered') {
      return 'bg-gray-50 opacity-80';
    }
    if (shipment?.status === 'annule' || shipment?.status === 'exception') {
      return 'bg-red-50/30 opacity-80';
    }
    return 'hover:bg-green-50/40';
  };

  // ─── Shipment status badge ───────────────────────────────────────────────
  const getShipmentStatusBadge = (status) => {
    const map = {
      en_attente:        { label: 'En attente',       bg: 'bg-yellow-50',  text: 'text-yellow-700',  dot: 'bg-yellow-400' },
      depose:            { label: 'Déposé',            bg: 'bg-blue-50',    text: 'text-blue-700',    dot: 'bg-blue-400' },
      en_preparation:    { label: 'En préparation',   bg: 'bg-indigo-50',  text: 'text-indigo-700',  dot: 'bg-indigo-400' },
      stocke:            { label: 'Stocké',            bg: 'bg-gray-100',   text: 'text-gray-700',    dot: 'bg-gray-400' },
      charge_conteneur:  { label: 'Chargé conteneur', bg: 'bg-purple-50',  text: 'text-purple-700',  dot: 'bg-purple-400' },
      au_port_anvers:    { label: 'Port Anvers',       bg: 'bg-cyan-50',    text: 'text-cyan-700',    dot: 'bg-cyan-400' },
      bateau_parti:      { label: 'Bateau parti',      bg: 'bg-sky-50',     text: 'text-sky-700',     dot: 'bg-sky-400' },
      bateau_en_mer:     { label: 'En mer',            bg: 'bg-sky-50',     text: 'text-sky-700',     dot: 'bg-sky-500' },
      en_transit:        { label: 'En transit',        bg: 'bg-sky-50',     text: 'text-sky-700',     dot: 'bg-sky-500' },
      arrive_abidjan:    { label: 'Arrivé Abidjan',   bg: 'bg-teal-50',    text: 'text-teal-700',    dot: 'bg-teal-400' },
      douane:            { label: 'Douane',            bg: 'bg-orange-50',  text: 'text-orange-700',  dot: 'bg-orange-400' },
      en_livraison:      { label: 'En livraison',      bg: 'bg-amber-50',   text: 'text-amber-700',   dot: 'bg-amber-400' },
      livre:             { label: 'Livré',             bg: 'bg-green-50',   text: 'text-green-700',   dot: 'bg-green-500' },
      delivered:         { label: 'Livré',             bg: 'bg-green-50',   text: 'text-green-700',   dot: 'bg-green-500' },
      annule:            { label: 'Annulé',            bg: 'bg-red-50',     text: 'text-red-700',     dot: 'bg-red-400' },
      exception:         { label: 'Exception',         bg: 'bg-red-50',     text: 'text-red-700',     dot: 'bg-red-400' },
    };
    const cfg = map?.[status] || { label: status || '—', bg: 'bg-gray-100', text: 'text-gray-600', dot: 'bg-gray-400' };
    return (
      <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${cfg?.bg} ${cfg?.text}`}>
        <span className={`w-1.5 h-1.5 rounded-full ${cfg?.dot}`} />
        {cfg?.label}
      </span>
    );
  };

  // ─── Payment status badge ────────────────────────────────────────────────
  const getPaymentBadge = (paymentStatus) => {
    const map = {
      paye:      { label: 'Payé',     bg: 'bg-green-100', text: 'text-green-700',  border: 'border-green-200' },
      partiel:   { label: 'Partiel',  bg: 'bg-orange-100',text: 'text-orange-700', border: 'border-orange-200' },
      non_paye:  { label: 'Non payé', bg: 'bg-red-100',   text: 'text-red-700',    border: 'border-red-200' },
    };
    const cfg = map?.[paymentStatus] || map?.['non_paye'];
    return (
      <span className={`inline-flex items-center px-2.5 py-1 rounded-md text-xs font-semibold border ${cfg?.bg} ${cfg?.text} ${cfg?.border}`}>
        {cfg?.label}
      </span>
    );
  };

  const getArticlesDisplay = () => {
    if (shipment?.articlesSummary) return shipment?.articlesSummary;
    if (shipment?.itemsCount > 0) return `${shipment?.itemsCount} article${shipment?.itemsCount > 1 ? 's' : ''}`;
    return <span className="text-gray-400 italic text-xs">—</span>;
  };

  const formatDate = (dateString) => {
    if (!dateString) return '—';
    return new Date(dateString)?.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' });
  };

  const formatAmount = (amount) => {
    if (!amount && amount !== 0) return '—';
    return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' })?.format(amount);
  };

  const handleRowClick = () => {
    if (shipment?.id) navigate(`/shipment-detail-management/${shipment?.id}`);
  };

  const handleView = (e) => {
    e?.stopPropagation();
    if (shipment?.reference) navigate(`/tracking/${shipment?.reference}`);
  };

  // ─── Archive ─────────────────────────────────────────────────────────────
  const handleArchiveConfirm = async () => {
    setActionLoading(true);
    setActionError('');
    try {
      await adminService?.archiveShipment(shipment?.id);
      setShowArchiveConfirm(false);
      onArchived?.(shipment?.id, true);
    } catch (err) {
      setActionError(err?.message || 'Erreur lors de l\'archivage');
    } finally {
      setActionLoading(false);
    }
  };

  // ─── Restore ─────────────────────────────────────────────────────────────
  const handleRestoreConfirm = async () => {
    setActionLoading(true);
    setActionError('');
    try {
      await adminService?.restoreShipment(shipment?.id);
      setShowRestoreConfirm(false);
      onArchived?.(shipment?.id, false);
    } catch (err) {
      setActionError(err?.message || 'Erreur lors de la restauration');
    } finally {
      setActionLoading(false);
    }
  };

  // ─── Delete (admin only) ─────────────────────────────────────────────────
  const handleDeleteClick = async () => {
    setMenuOpen(false);
    setActionError('');
    setDeleteBlockedByHistory(false);
    try {
      const deps = await adminService?.checkShipmentDependencies(shipment?.id);
      if (deps?.hasHistory) {
        setDeleteBlockedByHistory(true);
      }
      setShowDeleteConfirm(true);
    } catch (err) {
      setActionError(err?.message);
      setShowDeleteConfirm(true);
    }
  };

  const handleDeleteConfirm = async () => {
    if (deleteBlockedByHistory) return;
    setActionLoading(true);
    setActionError('');
    try {
      await adminService?.deleteShipment(shipment?.id);
      setShowDeleteConfirm(false);
      onDeleted?.(shipment?.id);
    } catch (err) {
      setActionError(err?.message || 'Erreur lors de la suppression');
    } finally {
      setActionLoading(false);
    }
  };

  const closeAllModals = () => {
    setShowArchiveConfirm(false);
    setShowRestoreConfirm(false);
    setShowDeleteConfirm(false);
    setActionError('');
    setDeleteBlockedByHistory(false);
  };

  return (
    <>
      <tr
        className={`transition-colors cursor-pointer group ${getRowClass()}`}
        onClick={handleRowClick}
      >
        {/* Référence */}
        <td className="px-3 py-3.5">
          <div className="flex items-center gap-2">
            <div className={`w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0 ${isTerminal || isArchived ? 'bg-gray-100' : 'bg-green-100'}`}>
              <Icon name="Package" size={14} className={isTerminal || isArchived ? 'text-gray-500' : 'text-green-700'} />
            </div>
            <div>
              <span className={`font-semibold text-sm tracking-wide ${isTerminal || isArchived ? 'text-gray-500' : 'text-gray-900'}`}>
                {shipment?.reference || '—'}
              </span>
              {isArchived && (
                <div className="text-xs text-gray-400 mt-0.5 flex items-center gap-1">
                  <Icon name="Archive" size={10} />
                  Archivé
                </div>
              )}
            </div>
          </div>
        </td>
        {/* Expéditeur */}
        <td className="px-3 py-3.5">
          <div className={`text-sm font-medium leading-tight ${isTerminal || isArchived ? 'text-gray-500' : 'text-gray-900'}`}>
            {shipment?.senderName || '—'}
          </div>
          {shipment?.senderCity && (
            <div className="text-xs text-gray-400 mt-0.5 flex items-center gap-1">
              <Icon name="MapPin" size={10} className="text-gray-400" />
              {shipment?.senderCity}
            </div>
          )}
          {shipment?.senderPhone && (
            <div className="text-xs text-gray-400 mt-0.5">{shipment?.senderPhone}</div>
          )}
        </td>
        {/* Destinataire */}
        <td className="px-3 py-3.5">
          <div className={`text-sm font-medium leading-tight ${isTerminal || isArchived ? 'text-gray-500' : 'text-gray-900'}`}>
            {shipment?.receiverName || '—'}
          </div>
          {shipment?.receiverPhone && (
            <div className="text-xs text-gray-400 mt-0.5">{shipment?.receiverPhone}</div>
          )}
        </td>
        {/* Ville destination */}
        <td className="px-3 py-3.5">
          {shipment?.receiverCity ? (
            <div className="flex items-center gap-1.5">
              <Icon name="MapPin" size={13} className={isTerminal || isArchived ? 'text-gray-400' : 'text-green-600'} />
              <span className={`text-sm font-medium ${isTerminal || isArchived ? 'text-gray-500' : 'text-gray-700'}`}>{shipment?.receiverCity}</span>
            </div>
          ) : (
            <span className="text-gray-400 text-xs">—</span>
          )}
        </td>
        {/* Articles */}
        <td className="px-3 py-3.5">
          <div className="flex items-center gap-1.5">
            <Icon name="Box" size={13} className="text-gray-400 flex-shrink-0" />
            <span className={`text-sm ${isTerminal || isArchived ? 'text-gray-500' : 'text-gray-700'}`}>{getArticlesDisplay()}</span>
          </div>
        </td>
        {/* Statut expédition */}
        <td className="px-3 py-3.5" onClick={(e) => e?.stopPropagation()}>
          {getShipmentStatusBadge(shipment?.status)}
        </td>
        {/* Paiement */}
        <td className="px-3 py-3.5" onClick={(e) => e?.stopPropagation()}>
          {getPaymentBadge(shipment?.paymentStatus)}
        </td>
        {/* Montant TTC */}
        <td className="px-3 py-3.5">
          <span className={`text-sm font-semibold ${isTerminal || isArchived ? 'text-gray-500' : 'text-gray-900'}`}>
            {formatAmount(shipment?.totalAmount)}
          </span>
        </td>
        {/* Date création */}
        <td className="px-3 py-3.5">
          <span className="text-xs text-gray-500 whitespace-nowrap">
            {formatDate(shipment?.createdAt)}
          </span>
        </td>
        {/* Actions — dropdown ⋮ */}
        <td className="px-3 py-3.5" onClick={(e) => e?.stopPropagation()}>
          <div className="relative" ref={menuRef}>
            <button
              onClick={(e) => { e?.stopPropagation(); setMenuOpen(prev => !prev); }}
              className="p-1.5 rounded-lg text-gray-500 hover:bg-gray-100 hover:text-gray-800 transition-colors"
              title="Actions"
            >
              <Icon name="MoreVertical" size={16} />
            </button>

            {menuOpen && (
              <div className="absolute right-0 top-8 z-50 bg-white border border-gray-200 rounded-xl shadow-lg min-w-[170px] py-1 overflow-hidden">
                {/* Voir détails */}
                <button
                  onClick={(e) => { e?.stopPropagation(); setMenuOpen(false); navigate(`/shipment-detail-management/${shipment?.id}`); }}
                  className="w-full flex items-center gap-2.5 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                >
                  <Icon name="Eye" size={14} className="text-blue-500" />
                  Voir détails
                </button>

                {/* Tracking */}
                <button
                  onClick={(e) => { e?.stopPropagation(); setMenuOpen(false); handleView(e); }}
                  className="w-full flex items-center gap-2.5 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                >
                  <Icon name="MapPin" size={14} className="text-green-500" />
                  Voir tracking
                </button>

                <div className="border-t border-gray-100 my-1" />

                {/* Archiver / Restaurer */}
                {!isArchived ? (
                  <button
                    onClick={(e) => { e?.stopPropagation(); setMenuOpen(false); setShowArchiveConfirm(true); }}
                    className="w-full flex items-center gap-2.5 px-4 py-2.5 text-sm text-gray-700 hover:bg-amber-50 transition-colors"
                  >
                    <Icon name="Archive" size={14} className="text-amber-500" />
                    Archiver
                  </button>
                ) : (
                  <button
                    onClick={(e) => { e?.stopPropagation(); setMenuOpen(false); setShowRestoreConfirm(true); }}
                    className="w-full flex items-center gap-2.5 px-4 py-2.5 text-sm text-gray-700 hover:bg-green-50 transition-colors"
                  >
                    <Icon name="ArchiveRestore" size={14} className="text-green-500" />
                    Restaurer
                  </button>
                )}

                {/* Supprimer — admin only */}
                {isAdmin && (
                  <>
                    <div className="border-t border-gray-100 my-1" />
                    <button
                      onClick={(e) => { e?.stopPropagation(); handleDeleteClick(); }}
                      className="w-full flex items-center gap-2.5 px-4 py-2.5 text-sm text-red-600 hover:bg-red-50 transition-colors"
                    >
                      <Icon name="Trash2" size={14} className="text-red-500" />
                      Supprimer
                    </button>
                  </>
                )}
              </div>
            )}
          </div>
        </td>
      </tr>

      {/* ── Archive Confirmation Modal ─────────────────────────────────── */}
      {showArchiveConfirm && (
        <tr>
          <td colSpan={10} className="p-0">
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" onClick={() => !actionLoading && closeAllModals()}>
              <div className="bg-white rounded-xl shadow-2xl p-6 max-w-md w-full mx-4" onClick={(e) => e?.stopPropagation()}>
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0">
                    <Icon name="Archive" size={20} className="text-amber-600" />
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-gray-900">Archiver l'envoi</h3>
                    <p className="text-sm text-gray-500">{shipment?.reference}</p>
                  </div>
                </div>
                <p className="text-gray-700 mb-4">
                  L'envoi sera masqué de la vue <strong>Actifs</strong> mais restera conservé dans la base de données avec toutes ses données (factures, paiements, historique, documents).
                </p>
                {actionError && (
                  <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">{actionError}</div>
                )}
                <div className="flex gap-3">
                  <button onClick={closeAllModals} disabled={actionLoading} className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 font-medium disabled:opacity-50">
                    Annuler
                  </button>
                  <button onClick={handleArchiveConfirm} disabled={actionLoading} className="flex-1 px-4 py-2 bg-amber-500 text-white rounded-lg hover:bg-amber-600 font-medium disabled:opacity-50 flex items-center justify-center gap-2">
                    {actionLoading ? <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" /> : <Icon name="Archive" size={16} />}
                    Archiver
                  </button>
                </div>
              </div>
            </div>
          </td>
        </tr>
      )}

      {/* ── Restore Confirmation Modal ─────────────────────────────────── */}
      {showRestoreConfirm && (
        <tr>
          <td colSpan={10} className="p-0">
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" onClick={() => !actionLoading && closeAllModals()}>
              <div className="bg-white rounded-xl shadow-2xl p-6 max-w-md w-full mx-4" onClick={(e) => e?.stopPropagation()}>
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0">
                    <Icon name="ArchiveRestore" size={20} className="text-green-600" />
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-gray-900">Restaurer l'envoi</h3>
                    <p className="text-sm text-gray-500">{shipment?.reference}</p>
                  </div>
                </div>
                <p className="text-gray-700 mb-4">
                  L'envoi sera restauré et réapparaîtra dans la vue <strong>Actifs</strong>.
                </p>
                {actionError && (
                  <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">{actionError}</div>
                )}
                <div className="flex gap-3">
                  <button onClick={closeAllModals} disabled={actionLoading} className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 font-medium disabled:opacity-50">
                    Annuler
                  </button>
                  <button onClick={handleRestoreConfirm} disabled={actionLoading} className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 font-medium disabled:opacity-50 flex items-center justify-center gap-2">
                    {actionLoading ? <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" /> : <Icon name="ArchiveRestore" size={16} />}
                    Restaurer
                  </button>
                </div>
              </div>
            </div>
          </td>
        </tr>
      )}

      {/* ── Delete Confirmation Modal ──────────────────────────────────── */}
      {showDeleteConfirm && (
        <tr>
          <td colSpan={10} className="p-0">
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" onClick={() => !actionLoading && closeAllModals()}>
              <div className="bg-white rounded-xl shadow-2xl p-6 max-w-md w-full mx-4" onClick={(e) => e?.stopPropagation()}>
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0">
                    <Icon name="AlertTriangle" size={20} className="text-red-600" />
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-gray-900">Supprimer l'envoi</h3>
                    <p className="text-sm text-gray-500">{shipment?.reference}</p>
                  </div>
                </div>

                {deleteBlockedByHistory ? (
                  <>
                    <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg mb-4">
                      <p className="text-amber-800 text-sm font-medium">
                        Cet envoi possède un historique logistique ou financier. Il ne peut pas être supprimé définitivement. Vous pouvez l'archiver.
                      </p>
                    </div>
                    <div className="flex gap-3">
                      <button onClick={closeAllModals} className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 font-medium">
                        Fermer
                      </button>
                      <button
                        onClick={() => { closeAllModals(); setShowArchiveConfirm(true); }}
                        className="flex-1 px-4 py-2 bg-amber-500 text-white rounded-lg hover:bg-amber-600 font-medium flex items-center justify-center gap-2"
                      >
                        <Icon name="Archive" size={16} />
                        Archiver à la place
                      </button>
                    </div>
                  </>
                ) : (
                  <>
                    <p className="text-gray-700 mb-2">
                      Supprimer définitivement l'envoi <strong>{shipment?.reference}</strong> ? Cette action est irréversible.
                    </p>
                    <p className="text-xs text-gray-500 mb-4 bg-gray-50 rounded-lg p-3">
                      Seront supprimés : articles/colis, messages, événements, fichiers, notifications WhatsApp, tâches de livraison, données financières.
                    </p>
                    {actionError && (
                      <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">{actionError}</div>
                    )}
                    <div className="flex gap-3">
                      <button onClick={closeAllModals} disabled={actionLoading} className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 font-medium disabled:opacity-50">
                        Annuler
                      </button>
                      <button onClick={handleDeleteConfirm} disabled={actionLoading} className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 font-medium disabled:opacity-50 flex items-center justify-center gap-2">
                        {actionLoading ? <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" /> : <Icon name="Trash2" size={16} />}
                        Supprimer définitivement
                      </button>
                    </div>
                  </>
                )}
              </div>
            </div>
          </td>
        </tr>
      )}
    </>
  );
};

export default ShipmentTableRow;